import { useEffect, useMemo, useState } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../lib/auth';

// Volunteer portal: one focused page for a signed-in volunteer to see
// their status, total hours, and the list of OPEN activities they can
// self-register into. NO sidebar, no other admin sections. Everything
// here is filtered server-side by RLS to just this volunteer.

type Attendance = {
  id: number;
  event_id: number;
  event_date: string | null;
  shift_text: string | null;
  present: boolean;
  hours: number;
  attend_note: string | null;
  event: { name: string; location: string | null } | null;
};

type Registration = {
  id: number;
  event_id: number;
  status: 'PENDING' | 'CONFIRMED' | 'DECLINED';
  event: { id: number; name: string; event_date: string | null; location: string | null } | null;
};

type OpenEvent = {
  id: number;
  name: string;
  description: string | null;
  location: string | null;
  event_date: string | null;
  time_from: string | null;
  time_to: string | null;
};

const STATUS_LABEL = {
  REVIEW: 'قيد المراجعة',
  HR: 'لدى HR',
  ACCEPTED: 'مقبول',
  REJECTED: 'مرفوض',
} as const;

const STATUS_TINT = {
  REVIEW: '#ec9c1b',
  HR: '#2b7fea',
  ACCEPTED: '#1f9d55',
  REJECTED: '#d94a4a',
} as const;

export default function VolunteerPortalPage() {
  const { session, volunteer, signOut } = useAuth();
  const [attendance, setAttendance] = useState<Attendance[]>([]);
  const [regs, setRegs] = useState<Registration[]>([]);
  const [openEvents, setOpenEvents] = useState<OpenEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busyEvent, setBusyEvent] = useState<number | null>(null);

  useEffect(() => { void load(); }, [volunteer?.volunteer_id]);

  const load = async () => {
    if (!volunteer) return;
    setLoading(true);
    const [att, r, e] = await Promise.all([
      supabase
        .from('vol_attendance')
        .select('id, event_id, event_date, shift_text, present, hours, attend_note, event:vol_events(name, location)')
        .eq('volunteer_id', volunteer.volunteer_id)
        .order('event_date', { ascending: false }),
      supabase
        .from('vol_activity_registrations')
        .select('id, event_id, status, event:vol_events(id, name, event_date, location)')
        .eq('volunteer_id', volunteer.volunteer_id),
      supabase
        .from('vol_events')
        .select('id, name, description, location, event_date, time_from, time_to')
        .eq('is_open', true)
        .order('event_date'),
    ]);
    if (att.error) setError(att.error.message);
    else setAttendance((att.data ?? []) as unknown as Attendance[]);
    if (r.error) setError(r.error.message);
    else setRegs((r.data ?? []) as unknown as Registration[]);
    if (e.error) setError(e.error.message);
    else setOpenEvents((e.data ?? []) as OpenEvent[]);
    setLoading(false);
  };

  const totalHours = useMemo(
    () => attendance.filter((a) => a.present).reduce((sum, a) => sum + Number(a.hours || 0), 0),
    [attendance],
  );

  const attendedCount = useMemo(
    () => attendance.filter((a) => a.present).length,
    [attendance],
  );

  const registerFor = async (event_id: number) => {
    if (!volunteer) return;
    setBusyEvent(event_id);
    const { error: err } = await supabase.from('vol_activity_registrations').insert({
      volunteer_id: volunteer.volunteer_id,
      event_id,
    });
    setBusyEvent(null);
    if (err) return setError(err.message);
    await load();
  };

  const alreadyRegistered = (eventId: number) => regs.some((r) => r.event_id === eventId);

  if (!volunteer) {
    return <div className="center">جاري تحميل بياناتك...</div>;
  }

  const stTint = STATUS_TINT[volunteer.application_status];

  return (
    <div style={{ maxWidth: 900, margin: '0 auto', padding: '24px 20px 60px' }}>
      <header style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <img src={`${import.meta.env.BASE_URL}brand/mersal-logo.png`} alt="Mersal" style={{ width: 48, height: 48 }} />
          <div>
            <div style={{ fontWeight: 700, fontSize: 20 }}>أهلاً بيك، {volunteer.full_name}</div>
            <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>{session?.user.email}</div>
          </div>
        </div>
        <button className="btn btn-small btn-secondary" onClick={signOut}>خروج</button>
      </header>

      {error && <div className="error-inline">{error}</div>}

      {/* Status + KPIs */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12, marginBottom: 24 }}>
        <div className="card" style={{ borderInlineStart: `4px solid ${stTint}` }}>
          <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>حالة الطلب</div>
          <div style={{ fontWeight: 700, fontSize: 18, marginTop: 4, color: stTint }}>
            {STATUS_LABEL[volunteer.application_status]}
          </div>
        </div>
        <div className="card">
          <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>إجمالي الساعات</div>
          <div style={{ fontWeight: 700, fontSize: 28, marginTop: 4 }}>{totalHours.toFixed(1)}</div>
        </div>
        <div className="card">
          <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>عدد الأنشطة</div>
          <div style={{ fontWeight: 700, fontSize: 28, marginTop: 4 }}>{attendedCount}</div>
        </div>
        <div className="card">
          <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>تسجيلات مفتوحة</div>
          <div style={{ fontWeight: 700, fontSize: 28, marginTop: 4 }}>
            {regs.filter((r) => r.status !== 'DECLINED').length}
          </div>
        </div>
      </div>

      {/* Attendance history */}
      <section style={{ marginBottom: 24 }}>
        <h2 style={{ marginBottom: 12 }}>سجل حضوري وساعاتي</h2>
        {loading ? (
          <div className="empty">جاري التحميل...</div>
        ) : attendance.length === 0 ? (
          <div className="card empty">لسه ما فيش نشاط حضرتيه — بمجرد ما تحضر أي نشاط، ساعاتك هتتسجل هنا.</div>
        ) : (
          <table className="table">
            <thead>
              <tr>
                <th>النشاط</th>
                <th>التاريخ</th>
                <th>الوردية</th>
                <th>حضرت؟</th>
                <th>ساعات</th>
                <th>ملاحظات</th>
              </tr>
            </thead>
            <tbody>
              {attendance.map((a) => (
                <tr key={a.id}>
                  <td>
                    <div>{a.event?.name ?? '—'}</div>
                    <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{a.event?.location ?? ''}</div>
                  </td>
                  <td>{a.event_date ?? '—'}</td>
                  <td>{a.shift_text ?? '—'}</td>
                  <td>
                    <span className={`badge ${a.present ? 'badge-success' : 'badge-muted'}`}>
                      {a.present ? 'نعم' : 'لأ'}
                    </span>
                  </td>
                  <td><strong>{Number(a.hours || 0).toFixed(1)}</strong></td>
                  <td>{a.attend_note ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      {/* My upcoming registrations */}
      {regs.length > 0 && (
        <section style={{ marginBottom: 24 }}>
          <h2 style={{ marginBottom: 12 }}>الأنشطة اللي مسجلة فيها</h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 12 }}>
            {regs.map((r) => (
              <div key={r.id} className="card">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
                  <strong>{r.event?.name ?? '—'}</strong>
                  <span className={`badge ${r.status === 'CONFIRMED' ? 'badge-success' : r.status === 'DECLINED' ? 'badge-danger' : 'badge-pending'}`}>
                    {r.status === 'CONFIRMED' ? 'مؤكد' : r.status === 'DECLINED' ? 'اعتذرت' : 'بانتظار التأكيد'}
                  </span>
                </div>
                <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 4 }}>
                  {r.event?.event_date ?? '—'} · {r.event?.location ?? '—'}
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Open activities to register into */}
      {volunteer.application_status === 'ACCEPTED' && (
        <section>
          <h2 style={{ marginBottom: 12 }}>الأنشطة المتاحة للتسجيل</h2>
          {openEvents.length === 0 ? (
            <div className="card empty">مفيش أنشطة مفتوحة دلوقتي — تابع الصفحة، هتظهر لك أول ما تفتح.</div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 12 }}>
              {openEvents.map((e) => {
                const on = alreadyRegistered(e.id);
                return (
                  <div key={e.id} className="card">
                    <strong>{e.name}</strong>
                    {e.description && (
                      <div style={{ fontSize: 13, marginTop: 4 }}>{e.description}</div>
                    )}
                    <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 6 }}>
                      📅 {e.event_date ?? 'بدون تاريخ محدد'}
                      {e.time_from && ` · ${e.time_from}${e.time_to ? ' - ' + e.time_to : ''}`}
                    </div>
                    {e.location && (
                      <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>📍 {e.location}</div>
                    )}
                    <button
                      className="btn btn-small"
                      style={{ marginTop: 10 }}
                      disabled={on || busyEvent === e.id}
                      onClick={() => void registerFor(e.id)}
                    >
                      {on ? 'مسجل فيها' : busyEvent === e.id ? 'جاري التسجيل...' : 'سجل معانا'}
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </section>
      )}

      {volunteer.application_status !== 'ACCEPTED' && (
        <section>
          <div className="card" style={{ borderInlineStart: `4px solid ${stTint}` }}>
            <strong>طلبك لسه بيتراجع</strong>
            <div style={{ fontSize: 13, marginTop: 6, color: 'var(--text-secondary)' }}>
              بمجرد ما HR تعتمد طلبك، الأنشطة المفتوحة هتظهر لك هنا وتقدر تسجّل فيها.
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
